import React, { useContext, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { handleKeyEvents } from 'utils/handlers';
import { useAppSelector } from 'utils/hooks';
import { ShufflerContext } from './Shuffler';
import ShufflerProduct from './ShufflerProduct';

import type { ShufflerContextType } from './Shuffler';
import { notNull } from 'utils/type_guards';

type MobileProductDetailProps = {
  handleShowAdvancedFilters: (show: boolean) => void;
};

function MobileProductDetail(props: MobileProductDetailProps) {
  const { handleShowAdvancedFilters } = props;
  const {
    product,
    catalogOptionGroups,
    productOptionGroups,
    currentOptionGroup,
    relatedProducts,
    itemClick,
    optionGroupClick,
    optionClick,
    setCurrentOptionGroup,
    showRelatedProducts,
    setShowRelatedProducts,
    staticFilterData,
  } = useContext(ShufflerContext) as ShufflerContextType;

  function showMobileMetaSpecification(spec: keyof ShufflerProduct['metaData']) {
    let str;
    switch (spec) {
      case 'numphotos':
        str = `${product?.metaData[spec]} Images`;
        break;
      case 'hascustomtext':
        str = product?.metaData[spec] ? 'Custom text' : '';
        break;
      default:
        str = product?.metaData[spec].toString();
    }
    return str;
  }

  useEffect(() => {
    if (showRelatedProducts) {
      setCurrentOptionGroup(undefined);
    }
  }, [showRelatedProducts]);

  useEffect(() => {
    setShowRelatedProducts(currentOptionGroup === undefined);
  }, [currentOptionGroup]);

  const optionButtons = useMemo(() => {
    if (!currentOptionGroup || !product) return null;

    return (
      <div className="options_collapsible_content current_options">
        {currentOptionGroup.options.map((option) => (
          <div
            role="button"
            tabIndex={0}
            key={option.id}
            className={`option`}
            data-id={option.id}
            data-groupname={currentOptionGroup.name}
            onClick={optionClick}
            onKeyDown={handleKeyEvents({ open: optionClick })}
            title={option.name}
          >
            <img
              src={option.image_url}
              alt={`${currentOptionGroup.name} - ${option.name}`}
              title={`${currentOptionGroup.name} - ${option.name}`}
            />
            <div className="option_text">{option.name}</div>
          </div>
        ))}
      </div>
    );
  }, [currentOptionGroup]);

  if (!product) return null;

  return (
    <React.Fragment>
      <div className="left-side">
        <div className="mobile_product_detail_container">
          <div className="product_detail">
            <img
              id="product_detail_image"
              src={product.imageUrl}
              className="fadein"
              title={product.name}
              alt={product.name}
            />
          </div>

          <div className="product_specifications">
            <div className="product_subcatalog">{product.subcatalog}</div>
            {staticFilterData?.detailFilters.map((df) => (
              <div
                key={df}
                title={product[df.toLowerCase() as keyof ShufflerProduct]?.toString()}
                className="product_spec"
              >
                <span className="spec_value">
                  {product[df.toLowerCase() as keyof ShufflerProduct]?.toString()}
                </span>
              </div>
            ))}
            <div className="product_spec">
              <span className="spec_value">{showMobileMetaSpecification('numphotos')}</span>
            </div>
            <div className="product_spec">
              <span className="spec_value">{showMobileMetaSpecification('hascustomtext')}</span>
            </div>

            {productOptionGroups.length > 0 && (
              <div className="selected_options">
                {productOptionGroups.map((optionGroup) => (
                  <div key={optionGroup.uid} className="product_spec">
                    <span className="spec_name">{optionGroup.name}:</span>
                    <span className="spec_value option_text">
                      {/* {selectedOptions.get(optionGroup.name)
                        ? selectedOptions.get(optionGroup.name).name
                        : ''} */}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="button_container">
            <button
              onClick={() => handleShowAdvancedFilters(false)}
              data-id={product.id}
              title="Customize by adding your photos to this product"
            >
              Add Images & Text
            </button>
          </div>
        </div>
      </div>

      <div className="right-side">
        <div className="content_box">
          <div className="related_products options_collapsible">
            <button
              className="options_collapsible_title"
              onClick={() => {
                setShowRelatedProducts(!showRelatedProducts);
              }}
            >
              <h2>Size & Format</h2>
              <FontAwesomeIcon icon={showRelatedProducts ? 'chevron-up' : 'chevron-down'} />
            </button>
            {showRelatedProducts && (
              <div className="options_collapsible_content">
                {relatedProducts.map(
                  (other, i, others) =>
                    others.length > 1 && (
                      <div
                        role="button"
                        tabIndex={0}
                        key={other.uid}
                        className={`related ${other.uid === product.uid ? 'active' : ''}`}
                        data-id={other.id}
                        data-catalog={other.subcatalog}
                        onClick={itemClick}
                        onKeyDown={handleKeyEvents({ open: itemClick })}
                      >
                        <img
                          src={other.imageUrl}
                          alt={`${other.name}\n${other.size}}\n${other.orientation}`}
                          title={`${other.name}\n${other.size}\n${other.orientation}`}
                        />
                      </div>
                    )
                )}
              </div>
            )}
          </div>

          {catalogOptionGroups.length > 0 && (
            <div className="option_groups_list">
              <div className="option_groups_button_container">
                {productOptionGroups.length === 0 && '...Loading product options'}
                {productOptionGroups.length > 0 &&
                  productOptionGroups.map((optionGroup) => (
                    <div key={optionGroup.id} className="options_collapsible">
                      <div
                        role="button"
                        tabIndex={0}
                        className="options_collapsible_title"
                        data-groupname={optionGroup.name}
                        onClick={optionGroupClick}
                        onKeyDown={handleKeyEvents({ open: optionGroupClick })}
                      >
                        <h2>{optionGroup.name}</h2>

                        <FontAwesomeIcon
                          icon={
                            currentOptionGroup && currentOptionGroup.name === optionGroup.name
                              ? 'chevron-up'
                              : 'chevron-down'
                          }
                        />
                      </div>
                      {currentOptionGroup &&
                        currentOptionGroup.name === optionGroup.name &&
                        optionButtons}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}

export default MobileProductDetail;
